import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronUp, ChevronDown, X, Plus } from 'lucide-react';

/**
 * Autocomplete tag picker.
 *
 * Props:
 *   tags          : array of {id, name} – all tags available for the project
 *   selectedIds   : array of tag ids currently selected
 *   onChange(ids) : called with new array of ids
 *   onCreateTag(name) : async function – should create the tag in the backend
 *                       and return the newly created {id, name}
 *   placeholder   : string (optional)
 */
export default function TagInput({ tags = [], selectedIds = [], onChange, onCreateTag, placeholder = 'Digite para criar ou selecionar tag' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // sort tags alphabetically (case-insensitive, locale-aware)
  const sorted = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })),
    [tags]
  );

  const selectedTags = useMemo(
    () => selectedIds.map((id) => tags.find((t) => t.id === id)).filter(Boolean),
    [selectedIds, tags]
  );

  const q = query.trim();
  const filtered = useMemo(() => {
    if (!q) return sorted;
    const needle = q.toLowerCase();
    return sorted.filter((t) => t.name.toLowerCase().includes(needle));
  }, [sorted, q]);

  const exactMatch = useMemo(
    () => q && filtered.some((t) => t.name.toLowerCase() === q.toLowerCase()),
    [filtered, q]
  );

  const canCreate = q && !exactMatch && onCreateTag;
  // Total options shown = filtered tags + (maybe create option at end)
  const optionsCount = filtered.length + (canCreate ? 1 : 0);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Keep highlight in range when filtered list changes
  useEffect(() => {
    if (highlight >= optionsCount) setHighlight(Math.max(0, optionsCount - 1));
  }, [optionsCount, highlight]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  const handleCreate = async () => {
    if (!q || !onCreateTag || creating) return;
    setCreating(true);
    try {
      const newTag = await onCreateTag(q);
      if (newTag?.id != null) onChange([...selectedIds, newTag.id]);
      setQuery('');
    } catch (e) {
      // parent should toast on error
    } finally {
      setCreating(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) setOpen(true);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(optionsCount - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < filtered.length) {
        const t = filtered[highlight];
        if (t) toggle(t.id);
      } else if (canCreate) {
        handleCreate();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selectedIds.length > 0) {
      // remove the last chip
      onChange(selectedIds.slice(0, -1));
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Chips above input */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedTags.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
              {t.name}
              <button type="button" onClick={() => toggle(t.id)} className="hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded p-0.5" title="Remover">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div
        className={`relative flex items-center border rounded-lg bg-white dark:bg-slate-700 ${open ? 'border-indigo-500 ring-2 ring-indigo-500/30' : 'border-slate-200 dark:border-slate-600'}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 bg-transparent outline-none text-slate-700 dark:text-slate-100 text-sm placeholder-slate-400 dark:placeholder-slate-500"
        />
        <button type="button" onClick={() => { setOpen((v) => !v); inputRef.current?.focus(); }}
          className="px-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400 text-center">
              Nenhuma tag encontrada.
            </div>
          )}
          {filtered.map((t, idx) => {
            const selected = selectedIds.includes(t.id);
            const active = idx === highlight;
            return (
              <button
                key={t.id}
                type="button"
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => toggle(t.id)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${active ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''} ${selected ? 'text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-slate-200'}`}
              >
                <span className="truncate">{t.name}</span>
                {selected && <span className="text-xs text-indigo-600 dark:text-indigo-400">selecionada</span>}
              </button>
            );
          })}
          {canCreate && (
            <button
              type="button"
              onMouseEnter={() => setHighlight(filtered.length)}
              onClick={handleCreate}
              disabled={creating}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-t border-slate-100 dark:border-slate-700 ${highlight === filtered.length ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''} text-indigo-700 dark:text-indigo-300 font-medium`}
            >
              <Plus className="w-4 h-4" />
              <span className="truncate">Criar "{q}"</span>
              <span className="ml-auto text-xs text-slate-400">Enter</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
